/**
 * Utility functions to fetch token balances from Ink Explorer API
 * This provides a more efficient way to get all tokens held by an address
 * compared to querying each token individually via RPC calls
 * 
 * Enhanced with Blockscout API key authentication for higher rate limits
 */

import { config } from './config';
import { getBlockscoutHeaders, getAddressTokenBalances } from './blockscoutApi';

export interface ExplorerTokenBalance {
  tokenAddress?: string;
  contractAddress?: string;
  address?: string;
  balance?: string; // Raw balance string (will need decimals to format)
  value?: string;
  token?: {
    address?: string;
    contractAddress?: string;
    decimals?: number;
    name?: string;
    symbol?: string;
    logoUri?: string;
  };
  decimals?: number;
  name?: string;
  symbol?: string;
  logoUri?: string;
  logo?: string;
}

export interface ExplorerTokenResponse {
  status?: string;
  message?: string;
  result?: ExplorerTokenBalance[] | string;
  items?: ExplorerTokenBalance[];
  data?: ExplorerTokenBalance[];
}

/**
 * Fetch all token balances for an address from Ink Explorer API
 * Now uses unified Blockscout API with authentication as primary method
 * Falls back to legacy endpoints if needed
 */
export async function fetchTokenBalancesFromExplorer(
  address: string
): Promise<ExplorerTokenBalance[]> {
  const explorerBaseUrl = config.ink.explorerUrl;
  const normalizedAddress = address.toLowerCase();

  // Try the new authenticated API v2 endpoint first (most reliable with API key)
  try {
    console.log(`[ExplorerAPI] Trying authenticated API v2 endpoint for ${normalizedAddress}`);
    
    const v2Tokens = await getAddressTokenBalances(normalizedAddress);
    
    if (v2Tokens.length > 0) {
      // Convert to ExplorerTokenBalance format for backward compatibility
      const normalizedTokens: ExplorerTokenBalance[] = v2Tokens.map(token => ({
        tokenAddress: token.token.address.toLowerCase(),
        balance: token.value,
        decimals: parseInt(token.token.decimals),
        symbol: token.token.symbol,
        name: token.token.name,
        logoUri: token.token.icon_url || undefined,
      }));

      // Filter out zero balances
      const validTokens = normalizedTokens.filter(
        (token) =>
          token.tokenAddress &&
          token.tokenAddress !== '0x0000000000000000000000000000000000000000' &&
          token.balance &&
          BigInt(token.balance || '0') > 0n
      );

      if (validTokens.length > 0) {
        console.log(`[ExplorerAPI] Successfully fetched ${validTokens.length} tokens from API v2`);
        return validTokens;
      }
    }
  } catch (error) {
    console.warn('[ExplorerAPI] API v2 failed, falling back to legacy endpoints:', error);
  }

  // Fallback to legacy API endpoint patterns (with API key in headers)
  const apiEndpoints = [
    // BlockScout token list API (returns tokens with balances > 0)
    `${explorerBaseUrl}/api?module=account&action=tokenlist&address=${normalizedAddress}`,
    // REST API v2 pattern (direct)
    `${explorerBaseUrl}/api/v2/addresses/${normalizedAddress}/tokens`,
  ];

  for (const endpoint of apiEndpoints) {
    try {
      console.log(`[ExplorerAPI] Trying endpoint: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: getBlockscoutHeaders(), // Use authenticated headers
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });

      if (!response.ok) {
        console.warn(`[ExplorerAPI] Endpoint returned ${response.status}: ${endpoint}`);
        continue;
      }

      const data: ExplorerTokenResponse = await response.json();

      // BlockScout API returns {status: "1", message: "OK", result: [...]} for success
      // Status "0" means error, status "1" means success
      if (data.status === '0') {
        console.warn(`[ExplorerAPI] API returned error: ${data.message || 'Unknown error'}`);
        continue;
      }

      // Handle different response formats
      let tokens: ExplorerTokenBalance[] = [];

      if (data.result && Array.isArray(data.result)) {
        tokens = data.result;
      } else if (data.items && Array.isArray(data.items)) {
        tokens = data.items;
      } else if (data.data && Array.isArray(data.data)) {
        tokens = data.data;
      } else if (Array.isArray(data)) {
        tokens = data as ExplorerTokenBalance[];
      }

      // Normalize token addresses - handle different response formats
      const normalizedTokens = tokens.map((token) => {
        const tokenAddress =
          token.tokenAddress ||
          token.contractAddress ||
          token.address ||
          token.token?.address ||
          token.token?.contractAddress;
        
        const balance = token.balance || token.value || '0';
        
        return {
          ...token,
          tokenAddress: tokenAddress ? tokenAddress.toLowerCase() : undefined,
          balance,
          decimals: token.decimals || token.token?.decimals,
          symbol: token.symbol || token.token?.symbol,
          name: token.name || token.token?.name,
          logoUri: token.logoUri || token.logo || token.token?.logoUri,
        };
      });

      // Filter out zero balances and ensure we have valid token addresses
      const validTokens = normalizedTokens.filter(
        (token) =>
          token.tokenAddress &&
          token.tokenAddress !== '0x0000000000000000000000000000000000000000' &&
          token.balance &&
          BigInt(token.balance || '0') > 0n
      );

      if (validTokens.length > 0) {
        console.log(`[ExplorerAPI] Successfully fetched ${validTokens.length} tokens from ${endpoint}`);
        return validTokens;
      } else {
        console.warn(`[ExplorerAPI] Endpoint returned empty or invalid data: ${endpoint}`);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`[ExplorerAPI] Request timeout for ${endpoint}`);
      } else {
        console.warn(`[ExplorerAPI] Error with endpoint ${endpoint}:`, error);
      }
      continue;
    }
  }

  // If all endpoints failed, return empty array
  console.warn('[ExplorerAPI] All API endpoints failed, returning empty array');
  return [];
}

/**
 * Fetch token metadata (decimals, symbol, name) from explorer
 * Now uses authenticated API v2 endpoint as primary method
 * Falls back to RPC if explorer doesn't provide full metadata
 */
export async function getTokenMetadataFromExplorer(
  tokenAddress: string
): Promise<{
  decimals?: number;
  symbol?: string;
  name?: string;
  logoUri?: string;
}> {
  const explorerBaseUrl = config.ink.explorerUrl;
  const normalizedAddress = tokenAddress.toLowerCase();

  // Try authenticated API v2 first
  try {
    const response = await fetch(
      `${explorerBaseUrl}/api/v2/tokens/${normalizedAddress}`,
      { headers: getBlockscoutHeaders() }
    );
    
    if (response.ok) {
      const data = await response.json();
      return {
        decimals: data.decimals ? parseInt(data.decimals) : undefined,
        symbol: data.symbol,
        name: data.name,
        logoUri: data.icon_url || data.logo,
      };
    }
  } catch (error) {
    console.warn('[ExplorerAPI] API v2 token metadata failed, trying legacy endpoints');
  }

  // Fallback to legacy endpoints
  try {
    const endpoints = [
      `${explorerBaseUrl}/api?module=token&action=getToken&contractaddress=${normalizedAddress}`,
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, { headers: getBlockscoutHeaders() });
        if (response.ok) {
          const data = await response.json();
          return {
            decimals: data.result?.decimals || data.decimals,
            symbol: data.result?.symbol || data.symbol,
            name: data.result?.name || data.name,
            logoUri: data.result?.logoUri || data.logoUri || data.logo,
          };
        }
      } catch (err) {
        continue;
      }
    }
  } catch (error) {
    console.warn(`[ExplorerAPI] Could not fetch token metadata for ${tokenAddress}`);
  }

  return {};
}

